import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { Button } from './ui/button';
import {
  LayoutDashboard, BookOpen, LogOut, Moon, Sun, Home, Brain, GraduationCap,
  Settings, ChevronDown, Users, Mic, ShoppingBag,
  Target, Calendar, CalendarDays, TrendingUp, Trophy, BookMarked, Award,
  Shield, Rss, Wallet2, Smartphone,
  PanelLeftClose, PanelLeftOpen, Tag, Heart, Zap, User
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';
import NotificationBell from './NotificationBell';
// iter186: replaced two FABs (FloatingRecorder + FloatingQuickActions)
// with one consolidated QuickActionsPanel. SupportChatFab moved out
// to App.js in iter191 so it appears on public routes too.
import QuickActionsPanel from './QuickActionsPanel';
// FirstTimeWalkthrough removed Feb 2026 — 5-step product tour was redundant
// with the OnboardingFlow aha demo and intercepted clicks on the new
// Dashboard tab strip. The OnboardingPage + OnboardingFlow now cover the
// first-session "what is this app" job.
import GestureTutorialOverlay from './GestureTutorialOverlay';
import PwaInstallNudge from './PwaInstallNudge';
import { useKeyboardShortcuts, KeyboardShortcutsHelp } from './KeyboardShortcuts';
import { useTranslation } from 'react-i18next';
import { useDraggableCorner } from '../hooks/useDraggableCorner';

const SIDEBAR_COLLAPSED_KEY = 'controlroom_sidebar_collapsed_v1';
const BROWSE_OPEN_KEY = 'controlroom_browse_open_v1';

// ⚠️  IMPORTANT — do NOT move SidebarLink / SidebarSection /
// CollapsibleSidebarSection back inside the Layout function. They are
// hoisted to module scope on purpose. Defining them inside the parent
// component creates a NEW component identity on every render, which
// causes React to unmount + mount the children, breaking AnimatePresence
// and the Connect-section toggle (regression caught in iter_147 / 148).
// If you need closure values (isActive, sidebarCollapsed), pass them as
// explicit props the way they're passed below.

// Hoisted to module scope so React doesn't treat them as new component
// types on every Layout render. Previously these were redefined inside
// Layout, causing AnimatePresence to remount its children on every render
// of the parent and breaking the Connect-section toggle in iter_147.
const SidebarLink = ({ item, isActive, sidebarCollapsed }) => {
  const Icon = item.icon;
  const active = isActive(item.path);
  // Wave-inspired soft pill highlight (iter184) — replaces the hard
  // left-border bar with a rounded tinted background. Active items get
  // the pale Klein-blue wash (`wave-nav-item--active`); hover items get
  // the same wash at 5% opacity via the base `wave-nav-item` class.
  return (
    <Link
      to={item.path}
      title={item.tip}
      aria-label={sidebarCollapsed ? item.label : undefined}
      data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div
        className={`wave-nav-item ${active ? 'wave-nav-item--active' : ''} ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
        data-active={active ? 'true' : 'false'}
      >
        <Icon className="w-4 h-4 flex-shrink-0" aria-hidden />
        {!sidebarCollapsed && (
          <span className="text-sm truncate">{item.label}</span>
        )}
        {!sidebarCollapsed && item.badge && (
          <span className="wave-badge-new ml-auto">{item.badge}</span>
        )}
      </div>
    </Link>
  );
};

const SidebarSection = ({ section, isActive, sidebarCollapsed }) => (
  <div className="mb-4">
    {!sidebarCollapsed && (
      <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">
        {section.label}
      </p>
    )}
    <div className="space-y-0.5">
      {section.items.map((item) => (
        <SidebarLink key={item.path} item={item} isActive={isActive} sidebarCollapsed={sidebarCollapsed} />
      ))}
    </div>
  </div>
);

const CollapsibleSidebarSection = ({ section, open, onToggle, isActive, sidebarCollapsed }) => {
  const isAnyActive = section.items.some((i) => isActive(i.path));
  return (
    <div className="mb-4">
      {!sidebarCollapsed ? (
        <button
          type="button"
          onClick={onToggle}
          className={`w-full flex items-center justify-between px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${
            open || isAnyActive ? 'text-[var(--isp-heading)]' : 'text-[var(--isp-muted)] hover:text-[var(--isp-body)]'
          }`}
          data-testid={`sidebar-section-toggle-${section.label.toLowerCase()}`}
          aria-expanded={open}
        >
          <span>{section.label}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-0' : '-rotate-90'}`} />
        </button>
      ) : (
        <div className="px-2 mb-1 h-px bg-[var(--isp-glass-border)]" />
      )}
      <AnimatePresence initial={false}>
        {(open || sidebarCollapsed) && (
          <motion.div
            key={`browse-${section.label}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <SidebarLink key={item.path} item={item} isActive={isActive} sidebarCollapsed={sidebarCollapsed} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { updatePreference } = usePreferences();
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  // Browse section (Marketplace + Tutor Connect + Wallet) — closed by default to
  // keep the sidebar focused on STUDY actions. User can open it explicitly.
  const [browseOpen, setBrowseOpen] = useState(() => {
    try { return localStorage.getItem(BROWSE_OPEN_KEY) === '1'; } catch { return false; }
  });
  // Per-section open state for the new "My Learning" 4-group layout.
  // Auto-opens the group containing the current route on first mount, so
  // a user on /lectures lands with "Study" expanded.
  const [studyOpen, setStudyOpen] = useState(() => {
    try { return localStorage.getItem('isp_sidebar_study_open') !== '0'; } catch { return true; }
  });
  const [practiceOpen, setPracticeOpen] = useState(() => {
    try { return localStorage.getItem('isp_sidebar_practice_open') === '1'; } catch { return false; }
  });
  const [trackOpen, setTrackOpen] = useState(() => {
    try { return localStorage.getItem('isp_sidebar_track_open') === '1'; } catch { return false; }
  });
  const { showHelp, setShowHelp } = useKeyboardShortcuts();

  const isAdmin = !!user?.is_admin;

  useEffect(() => { setIsMobileMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0'); } catch {}
  }, [sidebarCollapsed]);
  useEffect(() => {
    try { localStorage.setItem(BROWSE_OPEN_KEY, browseOpen ? '1' : '0'); } catch {}
  }, [browseOpen]);
  useEffect(() => {
    try { localStorage.setItem('isp_sidebar_study_open', studyOpen ? '1' : '0'); } catch {}
  }, [studyOpen]);
  useEffect(() => {
    try { localStorage.setItem('isp_sidebar_practice_open', practiceOpen ? '1' : '0'); } catch {}
  }, [practiceOpen]);
  useEffect(() => {
    try { localStorage.setItem('isp_sidebar_track_open', trackOpen ? '1' : '0'); } catch {}
  }, [trackOpen]);

  // My Learning — collapsed from 12 flat links into 4 groups (Feb 2026 UX
  // pass). Dashboard stays as a top-level link because it's the most-hit
  // destination; the other 11 destinations live inside three intent-based
  // collapsibles. The currently-active group auto-opens (see below).
  const dashboardLink = {
    path: '/app', icon: LayoutDashboard, label: 'Dashboard', tip: 'Your control room',
  };
  const studySection = {
    label: 'Study',
    items: [
      { path: '/subjects', icon: BookOpen, label: 'Subjects', tip: 'Course subjects' },
      { path: '/lectures', icon: Mic, label: 'Lectures', tip: 'Record & manage' },
      { path: '/document-uploads', icon: BookMarked, label: 'Documents', tip: 'Files & PDFs' },
      { path: '/focus', icon: Zap, label: 'Focus', tip: 'Distraction-free study session' },
    ],
  };
  const practiceSection = {
    label: 'Practice',
    items: [
      { path: '/study-buddy', icon: Brain, label: 'AI Tutor', tip: 'Chat with AI' },
      { path: '/exam-mode', icon: Target, label: 'Exam Mode', tip: 'AI-powered exam practice' },
      { path: '/schedule', icon: Calendar, label: 'Schedule', tip: 'Plan study sessions' },
      { path: '/semester-plan', icon: CalendarDays, label: 'Semester Plan', tip: 'AI study plan from your syllabus' },
    ],
  };
  const trackSection = {
    label: 'Track',
    items: [
      { path: '/progress', icon: TrendingUp, label: 'Progress', tip: 'Stats & analytics' },
      { path: '/achievements', icon: Trophy, label: 'Achievements', tip: 'Badges & XP' },
      { path: '/proof', icon: Award, label: 'Proof', tip: 'Mastery audit + daily commitment' },
      { path: '/pacts', icon: Heart, label: 'Study Pacts', tip: 'Mutual streak with a friend' },
    ],
  };

  // Backwards-compat: a few callers below still expect a single learning
  // section to iterate over (mobile bottom-nav, search palette, etc.).
  const learningSection = {
    label: 'My Learning',
    items: [dashboardLink, ...studySection.items, ...practiceSection.items, ...trackSection.items],
  };

  // Connect — social surfaces are intentionally one section deeper than
  // Learning. Reframes the app from "social platform" to "focus tool with
  // a small social layer."
  const marketSection = {
    label: 'Connect',
    items: [
      { path: '/study-rooms', icon: Users, label: 'Study Rooms', tip: 'Pomodoro with friends' },
      { path: '/study-groups', icon: Users, label: 'Study Groups', tip: 'Collaborative groups' },
      { path: '/feed', icon: Home, label: 'Discovery', tip: 'Updates from your network' },
      { path: '/marketplace', icon: ShoppingBag, label: 'Marketplace', tip: 'Buy & sell notes' },
      { path: '/tutors', icon: Users, label: 'Tutor Connect', tip: 'Find or become a tutor' },
      { path: '/wallet', icon: Wallet2, label: 'Wallet', tip: 'Earnings & payouts' },
    ],
  };

  // iter186: removed the sidebar Account section (Settings / Pricing /
  // Admin). Settings and Pricing now live in the top-right header user
  // dropdown only — there was no reason to surface them twice. Admin
  // also moved into the user dropdown, conditionally visible when
  // `user.is_admin` is true.

  const allItems = [...learningSection.items, ...marketSection.items];
  const isActive = (path) => path === '/app'
    ? location.pathname === '/app'
    : location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Auto-open whichever group contains the current route on mount.
  useEffect(() => {
    if (studySection.items.some((i) => isActive(i.path))) setStudyOpen(true);
    if (practiceSection.items.some((i) => isActive(i.path))) setPracticeOpen(true);
    if (trackSection.items.some((i) => isActive(i.path))) setTrackOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // iter196 Week-4 mobile polish — bottom nav rewritten to 4 surrounding
  // items + a centered raised Record FAB. Spec from the retention overhaul:
  // Today / Subjects / Record(FAB) / Profile. Old 5-item shop+settings
  // pattern moved to the More menu (lg+ users still see the full sidebar).
  const bottomNav = [
    { path: '/app',         icon: Home,       label: 'Today',    testid: 'bottom-nav-today' },
    { path: '/subjects',    icon: BookOpen,   label: 'Subjects', testid: 'bottom-nav-subjects' },
    { path: '/lectures',    icon: Mic,        label: 'Record',   testid: 'bottom-nav-record', isFab: true },
    { path: '/profile',     icon: User,       label: 'Profile',  testid: 'bottom-nav-profile' },
  ];

  // Show sidebar on protected app pages (when user is signed in).
  const showSidebar = !!user && location.pathname !== '/' && !location.pathname.startsWith('/auth/');

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--isp-page)' }}>
      {/* WCAG: skip-to-main for keyboard users. Invisible until focused. */}
      <a href="#main-content" className="sr-only-focusable">
        Skip to main content
      </a>
      {/* Desktop Control Room Sidebar */}
      {showSidebar && (
        <aside
          className={`hidden lg:flex flex-col fixed top-0 left-0 h-screen z-40 border-r border-[var(--isp-glass-border)] bg-[var(--isp-nav)] backdrop-blur-xl transition-[width] duration-200 ${
            sidebarCollapsed ? 'w-16' : 'w-60'
          }`}
          data-testid="control-room-sidebar"
        >
          {/* Logo / collapse toggle */}
          <div className={`flex items-center h-16 border-b border-[var(--isp-glass-border)] px-3 ${
            sidebarCollapsed ? 'justify-center' : 'justify-between'
          }`}>
            <Link
              to="/"
              className="flex items-center gap-2 min-w-0"
              data-testid="sidebar-logo"
              aria-label="iStudyPro home"
              title="iStudyPro home"
            >
              <img src="/logo-main.png" alt="" aria-hidden className="w-8 h-8 object-contain flex-shrink-0" />
              {!sidebarCollapsed && (
                <span className="text-lg font-black tracking-tight truncate">
                  <span className="text-[var(--isp-heading)]">iStudy</span>
                  <span className="text-[#002FA7]">Pro</span>
                </span>
              )}
            </Link>
            {!sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(true)}
                className="p-1.5 rounded-md text-[var(--isp-muted)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] transition-colors"
                title="Collapse sidebar"
                data-testid="sidebar-collapse-btn"
              >
                <PanelLeftClose className="w-4 h-4" />
              </button>
            )}
          </div>

          {sidebarCollapsed && (
            <button
              onClick={() => setSidebarCollapsed(false)}
              className="mx-auto mt-3 mb-1 p-1.5 rounded-md text-[var(--isp-muted)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] transition-colors"
              title="Expand sidebar"
              data-testid="sidebar-expand-btn"
            >
              <PanelLeftOpen className="w-4 h-4" />
            </button>
          )}

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-4 px-2" data-testid="control-room-nav">
            {/* "Discovery" moved into the Connect collapsible (Feb 2026 UX pass)
                so the top of the sidebar is reserved for Dashboard + My Learning. */}
            <SidebarLink item={dashboardLink} isActive={isActive} sidebarCollapsed={sidebarCollapsed} />
            {!sidebarCollapsed && (
              <div className="px-3 mt-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--isp-muted)]">
                My Learning
              </div>
            )}
            <CollapsibleSidebarSection
              section={studySection}
              open={studyOpen}
              onToggle={() => setStudyOpen((v) => !v)}
              isActive={isActive}
              sidebarCollapsed={sidebarCollapsed}
            />
            <CollapsibleSidebarSection
              section={practiceSection}
              open={practiceOpen}
              onToggle={() => setPracticeOpen((v) => !v)}
              isActive={isActive}
              sidebarCollapsed={sidebarCollapsed}
            />
            <CollapsibleSidebarSection
              section={trackSection}
              open={trackOpen}
              onToggle={() => setTrackOpen((v) => !v)}
              isActive={isActive}
              sidebarCollapsed={sidebarCollapsed}
            />
            <CollapsibleSidebarSection
              section={marketSection}
              open={browseOpen}
              onToggle={() => setBrowseOpen((v) => !v)}
              isActive={isActive}
              sidebarCollapsed={sidebarCollapsed}
            />
          </nav>

          {/* Sidebar footer — only a compact brand chip remains. The full
              user card moved into the top-right header dropdown (iter185)
              so the bottom of the sidebar isn't a duplicate identity
              surface. Pioneer status now travels with the header avatar. */}
          <div className="border-t border-[var(--isp-glass-border)] p-3">
            <p className={`text-[10px] text-[var(--isp-muted)] ${sidebarCollapsed ? 'text-center' : ''}`}>
              {sidebarCollapsed ? 'v1' : 'iStudyPro · v1'}
            </p>
          </div>
        </aside>
      )}

      {/* Right side: header + content (offset by sidebar width on desktop) */}
      <div className={`flex-1 min-w-0 flex flex-col ${
        showSidebar ? (sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60') : ''
      }`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[var(--isp-nav)] backdrop-blur-xl border-b border-[var(--isp-glass-border)]" data-testid="app-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 lg:h-16">
              {/* Left cluster: sidebar collapse toggle (desktop, signed-in)
                  + mobile/public logo. Header-level toggle (iter185) so
                  users don't have to mouse into the sidebar to expand it
                  when it's collapsed — matches Slack/Linear/Wave UX. */}
              {showSidebar && (
                <button
                  type="button"
                  onClick={() => setSidebarCollapsed((v) => !v)}
                  className="hidden lg:inline-flex items-center justify-center w-9 h-9 rounded-lg text-[var(--isp-muted)] hover:text-[#002FA7] hover:bg-[#002FA7]/5 transition-colors mr-2"
                  title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  data-testid="header-sidebar-toggle"
                >
                  {sidebarCollapsed
                    ? <PanelLeftOpen className="w-5 h-5" aria-hidden />
                    : <PanelLeftClose className="w-5 h-5" aria-hidden />}
                </button>
              )}

              {/* Mobile / public-page logo */}
              <div className="flex items-center gap-3 lg:hidden">
                <Link to="/" className="flex items-center gap-2" data-testid="logo-link" title="iStudyPro Home">
                  <img src="/logo-main.png" alt="iStudyPro" className="w-8 h-8 object-contain" />
                  <span className="text-lg font-black hidden sm:block whitespace-nowrap tracking-tight">
                    <span className="text-[var(--isp-heading)]">iStudy</span>
                    <span className="text-[#002FA7]">Pro</span>
                  </span>
                </Link>
              </div>
              {/* Public-page (no sidebar) desktop logo */}
              {!showSidebar && (
                <Link to="/" className="hidden lg:flex items-center gap-2 mr-6 flex-shrink-0" title="iStudyPro Home" data-testid="logo-link-desktop">
                  <img src="/logo-main.png" alt="iStudyPro" className="w-8 h-8 object-contain" />
                  <span className="text-lg font-black whitespace-nowrap tracking-tight">
                    <span className="text-[var(--isp-heading)]">iStudy</span>
                    <span className="text-[#002FA7]">Pro</span>
                  </span>
                </Link>
              )}

              <div className="flex-1" />

              {/* Right cluster */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost" size="icon"
                  aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                  onClick={() => {
                    // Single source of truth: updatePreference triggers
                    // applyPreferences → setThemeMode (in PreferencesContext).
                    // Calling toggleTheme() in addition created a stale-closure
                    // race that left the DOM out of sync with persisted state.
                    const next = theme === 'light' ? 'dark' : 'light';
                    toggleTheme();              // optimistic local flip
                    updatePreference('theme', next).catch(() => {});
                  }}
                  className="rounded-lg text-[var(--isp-muted)] hover:text-[#002FA7] hover:bg-[#002FA7]/5"
                  data-testid="theme-toggle"
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" aria-hidden /> : <Moon className="w-5 h-5" aria-hidden />}
                </Button>

                <NotificationBell />

                {/* User avatar + name + dropdown — top-right identity surface
                    (iter185). Replaces the bottom-of-sidebar user card so
                    profile/settings/logout are one click away from anywhere
                    in the app. Desktop only; mobile uses the bottom-nav. */}
                {user && showSidebar && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="hidden lg:inline-flex items-center gap-2 ml-1 pl-2 pr-3 py-1 rounded-full border border-transparent hover:border-[var(--isp-glass-border)] hover:bg-[var(--isp-glass-inner)] transition-colors"
                        data-testid="header-user-menu-trigger"
                        aria-label="Open user menu"
                      >
                        <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-[#002FA7] to-[#42A5F5] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {user?.name?.charAt(0).toUpperCase() || 'U'}
                          {user?.founder_pioneer && (
                            <span
                              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#FFD700] flex items-center justify-center shadow-sm ring-2 ring-[var(--isp-nav)]"
                              title="Founding Member"
                              data-testid="header-founder-badge"
                            >
                              <span className="text-[8px]">👑</span>
                            </span>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-[var(--isp-heading)] max-w-[8rem] truncate">
                          {user?.name?.split(' ')[0] || 'You'}
                        </span>
                        {user?.founder_pioneer && (
                          <span className="text-[9px] font-bold text-[#FFD700] tracking-wider">PIONEER</span>
                        )}
                        <ChevronDown className="w-3.5 h-3.5 text-[var(--isp-muted)]" aria-hidden />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="w-56 bg-[var(--isp-glass)] border border-[var(--isp-glass-border)]"
                      data-testid="header-user-menu"
                    >
                      <DropdownMenuLabel className="text-[var(--isp-heading)]">
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-semibold truncate">{user?.name || 'You'}</span>
                          <span className="text-[10px] text-[var(--isp-muted)] truncate font-normal">{user?.email || ''}</span>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-[var(--isp-glass-border)]" />
                      <DropdownMenuItem asChild>
                        <Link to="/app/profile" data-testid="header-menu-profile" className="cursor-pointer text-[var(--isp-body)] focus:text-[var(--isp-heading)]">
                          <User className="w-4 h-4 mr-2" aria-hidden /> Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/settings" data-testid="header-menu-settings" className="cursor-pointer text-[var(--isp-body)] focus:text-[var(--isp-heading)]">
                          <Settings className="w-4 h-4 mr-2" aria-hidden /> Settings
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/pricing" data-testid="header-menu-pricing" className="cursor-pointer text-[var(--isp-body)] focus:text-[var(--isp-heading)]">
                          <Tag className="w-4 h-4 mr-2" aria-hidden /> Pricing
                        </Link>
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem asChild>
                          <Link to="/app/admin" data-testid="header-menu-admin" className="cursor-pointer text-[var(--isp-body)] focus:text-[var(--isp-heading)]">
                            <Shield className="w-4 h-4 mr-2" aria-hidden /> Admin
                          </Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-[var(--isp-glass-border)]" />
                      <DropdownMenuItem
                        onClick={logout}
                        className="cursor-pointer text-red-500 focus:text-red-500 focus:bg-red-500/10"
                        data-testid="header-menu-logout"
                      >
                        <LogOut className="w-4 h-4 mr-2" aria-hidden /> Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Public-page (no sidebar) profile mini — kept for landing/auth surfaces */}
                {user && !showSidebar && (
                  <div className="hidden lg:flex items-center gap-2 ml-2 pl-2 border-l border-[var(--isp-divider)]">
                    <Link to="/app/profile" data-testid="header-profile-link" title="Your Profile">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#002FA7] to-[#42A5F5] flex items-center justify-center text-white font-bold text-sm hover:ring-2 hover:ring-[#002FA7]/30 transition-all cursor-pointer">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    </Link>
                    <Button aria-label="Sign out" variant="ghost" size="icon" onClick={logout} title="Sign out"
                      className="rounded-lg text-[var(--isp-muted)] hover:text-red-500 hover:bg-red-500/5" data-testid="logout-button">
                      <LogOut className="w-5 h-5" />
                    </Button>
                  </div>
                )}

                {/* Mobile menu toggle */}
                <Button aria-label="Expand" variant="ghost" size="icon"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden rounded-lg text-[var(--isp-body)] hover:bg-[var(--isp-glass-inner)]"
                  data-testid="mobile-menu-toggle"
                >
                  <ChevronDown className={`w-5 h-5 transition-transform ${isMobileMenuOpen ? 'rotate-180' : ''}`} />
                </Button>
              </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
              {isMobileMenuOpen && (
                <motion.nav initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="lg:hidden py-4 border-t border-[var(--isp-divider)]"
                  data-testid="mobile-menu"
                >
                  <div className="space-y-1">
                    <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${location.pathname === '/' ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'text-[var(--isp-body)]'}`}><Home className="w-4 h-4" /> Home</div>
                    </Link>
                    {user && allItems.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.path);
                      return (
                        <Link key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)}>
                          <div className={`flex items-center gap-3 px-4 py-2.5 text-sm ${active ? 'text-[#002FA7]' : 'text-[var(--isp-body)]'}`}><Icon className="w-4 h-4" /> {item.label}</div>
                        </Link>
                      );
                    })}
                    {user && (
                      <button
                        onClick={() => { setIsMobileMenuOpen(false); logout(); }}
                        className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/5"
                        data-testid="mobile-logout-btn"
                      >
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    )}
                    <div className="border-t border-[var(--isp-divider)] pt-2 mt-2">
                      <Link to="/download" onClick={() => setIsMobileMenuOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#002FA7]"><Smartphone className="w-4 h-4" /> Download Android App</div>
                      </Link>
                    </div>
                  </div>
                </motion.nav>
              )}
            </AnimatePresence>
          </div>
        </header>

        {/* Main content — iter196: extra mobile bottom padding (pb-32)
            so end-of-content controls (e.g. dashboard-customize-trigger
            FAB) clear the fixed bottom-nav z-area instead of being
            partially obscured by it. */}
        <main id="main-content" className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32 lg:pb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            {children}
          </motion.div>
        </main>
      </div>

      {/* Mobile Bottom Nav — iter196: 4 items + centered raised Record FAB.
          Touch targets are ≥56px (44pt min, retention spec ≥56px). Active
          dot indicator moved into the item label for accessibility.
          Gated behind showSidebar (which itself is `!!user`) so the nav
          doesn't appear on /login or other unauthenticated routes. */}
      {showSidebar && (
      <nav className="lg:hidden isp-bottom-nav" data-testid="mobile-bottom-nav" role="navigation" aria-label="Primary mobile navigation">
        {bottomNav.map((item) => {
          const Icon = item.icon;
          const isActiveBottom = item.path === '/app'
            ? location.pathname === '/app'
            : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          if (item.isFab) {
            // Centered, raised, primary-color FAB. Lifts above the
            // bottom-nav surface so it reads as the canonical action.
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-label={`Start ${item.label.toLowerCase()}`}
                className="isp-bottom-nav-fab"
                data-testid={item.testid}
              >
                <span className="isp-bottom-nav-fab-inner">
                  <Icon className="w-6 h-6" aria-hidden />
                </span>
                <span className="isp-bottom-nav-fab-label">{item.label}</span>
              </Link>
            );
          }
          return (
            <Link
              key={item.path}
              to={item.path}
              title={item.label}
              aria-label={item.label}
              aria-current={isActiveBottom ? 'page' : undefined}
              className={`isp-bottom-nav-item ${isActiveBottom ? 'active' : ''}`}
              data-testid={item.testid}
            >
              <Icon className="w-5 h-5" aria-hidden />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
      )}

      {/* Floating right-side stack (iter186 consolidation, iter191 hoist):
          QuickActionsPanel — authed-only collapsible 7-action launcher.
          SupportChatFab is now mounted globally in App.js so it appears
          on /login, /, /mobile, /pricing — anywhere a pre-login user
          might be stuck. */}
      <QuickActionsPanel />

      {/* iter196 — Day-3+ PWA install snackbar (mobile only). Auto-hides
          for users <3 days old or already running standalone. */}
      <PwaInstallNudge />

      <KeyboardShortcutsHelp isOpen={showHelp} onClose={() => setShowHelp(false)} />
      <GestureTutorialOverlay />
    </div>
  );
};

export default Layout;
